import { useContext, useMemo, useEffect, useLayoutEffect, useCallback } from "react"
import { useDispatch, useSelector } from "react-redux"
import { size, compact } from "lodash"
import { Paper } from "@mui/material"
import { Creators as NotificationCreators } from "modules/notifications/model"
import Error from "components/error"
import Loading from "components/loading"
import NoData from "components/intraday/no-data"
import { usePositionSetMetadata } from "resources/position-sets"
import { useModelMetadata } from "resources/models"
import { useContentSetsMetadata } from "resources/content-sets"
import { useActiveInstruments } from "resources/instruments"
import { useIntradayChannel, useLastIntradayBatch } from "hooks/intraday"
import { combineStatuses, OK, LOADING, ERROR } from "helpers/data-statuses"
import { makeFindAndGetBaseMetadata } from "helpers/base"
import { getIsDashboardIntradayFeatureEnabled } from "selectors/features"
import { DashboardContext } from "./context/dashboard"
import EmptyDashboard from "./components/empty"
import { useInstrumentsWithMetadata } from "./helpers"
import { useAnalytics } from "connected-components/analytics"
import { DASHBOARD_EVENTS } from "./analytics"
import { DashboardAnalyticsProvider } from "./analytics/provider"
import { useDefaultSelectedBase } from "./hooks/use-selected-base"
import useOpenUnifiedSearch from "./hooks/use-open-unified-search"
import Dashboard from "./dashboard"
import { RECENT_INSTRUMENT_SEARCH_TYPES } from "resources/instruments"
import { SHARED_EVENTS } from "connected-components/analytics/shared/events"
import "./style.scss"

function DashboardWrapper() {
  const dispatch = useDispatch()
  const analytics = useAnalytics()
  const { instrument: original, modelId, secondInstrument: rebalanced, comparisonInstrument: comparison, base, isPreferencesLoaded, setBase, isIntraday, setIsIntraday, setInstrument, setInstrumentsMetadata } = useContext(DashboardContext)
  const [modelMetadata, modelMetadataStatus] = useModelMetadata(modelId)
  const [instrumentMetadata, instrumentStatus] = usePositionSetMetadata(modelId, original, modelMetadata?.availability?.currentDate)
  const [comparisonInstrumentMetadata, comparisonInstrumentStatus] = usePositionSetMetadata(modelId, comparison, modelMetadata?.availability?.currentDate, { enabled: !!comparison })
  const [baseInstrumentMetadata, baseInstrumentStatus] = usePositionSetMetadata(modelId, base, modelMetadata?.availability?.currentDate, { enabled: !!base })
  // [TODO] What is this?
  // [TODO] Answer: It's combining instruments and instruments metadata.
  const [instrumentsWithMetadata, instrumentsWithMetadataStatus] = useInstrumentsWithMetadata(modelId, modelMetadata?.availability?.currentDate, { original, comparison, rebalanced, base })
  const [contentSets, contentSetsStatus] = useContentSetsMetadata()
  const hasMultipleInstruments = useMemo(() => size(compact([original, rebalanced, comparison])) > 1, [original, rebalanced, comparison])
  const [activeInstruments] = useActiveInstruments(modelId)

  const defaultSelectedBase = useDefaultSelectedBase(modelId)

  const status = useMemo(() =>
    combineStatuses([instrumentStatus, baseInstrumentStatus, modelMetadataStatus, comparisonInstrumentStatus, instrumentsWithMetadataStatus, contentSetsStatus]),
  [instrumentStatus, baseInstrumentStatus, modelMetadataStatus, comparisonInstrumentStatus, instrumentsWithMetadataStatus, contentSetsStatus])

  useEffect(() => {
    if (isPreferencesLoaded && modelMetadata && base && activeInstruments && !makeFindAndGetBaseMetadata(null, activeInstruments)(base)) {
      setBase(
        {
          id: defaultSelectedBase.id,
          type: defaultSelectedBase.type,
        },
        instrumentMetadata,
      )
      dispatch(NotificationCreators.add("warning", null, "A previously saved benchmark is no longer available; this has been replaced by a default benchmark."))
    }
  }, [isPreferencesLoaded, setBase, base, activeInstruments, defaultSelectedBase, dispatch, modelMetadata, instrumentMetadata])

  // TODO: This should probably be replaced/updated when Instrument Contexts are implemented
  useEffect(() => {
    setInstrumentsMetadata({
      original: instrumentMetadata,
      comparison: comparisonInstrumentMetadata,
      rebalanced: instrumentsWithMetadata?.rebalanced,
      base: baseInstrumentMetadata,
    })
  }, [instrumentMetadata, comparisonInstrumentMetadata, baseInstrumentMetadata, instrumentsWithMetadata?.rebalanced, setInstrumentsMetadata])

  const isDashboardIntradayFeatureEnabled = useSelector(getIsDashboardIntradayFeatureEnabled)

  // Use layout instead of useEffect to avoid rendering the page with the wrong mode
  useLayoutEffect(() => {
    if (isDashboardIntradayFeatureEnabled && instrumentMetadata?.intradayEnabled) {
      setIsIntraday(true)
    } else {
      setIsIntraday(false)
    }
  }, [isIntraday, instrumentMetadata, setIsIntraday, isDashboardIntradayFeatureEnabled])

  // we need to set up the intraday channel for the dashboard
  useIntradayChannel(isIntraday, original?.id, "dashboard")
  const [lastIntradayBatch, lastIntradayBatchStatus] = useLastIntradayBatch(original?.id)

  const onMainInstrumentSelection = useCallback(({ id, name, type, searchType, isInitialSelection }) => {
    const payload = { instrument: { id, name, type }, searchType }
    if (isInitialSelection) {
      analytics.track(DASHBOARD_EVENTS.INITIAL_INSTRUMENT_SELECTION, payload)
    } else {
      analytics.track(DASHBOARD_EVENTS.COMPARISON_SEARCH_SELECTION, payload)
    }
  }, [analytics])

  const openMainInstrumentSearch = useOpenUnifiedSearch(DashboardContext, setInstrument, RECENT_INSTRUMENT_SEARCH_TYPES.DASHBOARD, null, false, false, onMainInstrumentSelection)

  if (status === LOADING) {
    return <Loading />
  }

  if (status === ERROR) {
    analytics.track(SHARED_EVENTS.ERROR, { type: "data_loading_error", message: "Failed to load dashboard data" })

    return <Error />
  }

  if (!isPreferencesLoaded || status !== OK) {
    return <Loading />
  }

  if (!original) {
    return <EmptyDashboard openUnifiedSearch={openMainInstrumentSearch} />
  }

  if (!instrumentMetadata) {
    return <Loading />
  }

  if (isIntraday && instrumentMetadata?.intradayMetadata && !lastIntradayBatch) {
    return <Loading />
  } else if (isIntraday && instrumentMetadata?.intradayMetadata && !lastIntradayBatch?.id) {
    return <Paper className="details"><NoData autoRefresh /></Paper>
  }

  return (
    <Dashboard {...{
      modelMetadata,
      context: DashboardContext,
      instrumentMetadata,
      comparisonInstrumentMetadata,
      baseInstrumentMetadata,
      instrumentsWithMetadata,
      contentSets,
      hasMultipleInstruments,
      lastIntradayBatch,
      lastIntradayBatchStatus,
    }}
    />
  )
}

export default function DashboardWrapperWithAnalytics() {
  return (
    <DashboardAnalyticsProvider>
      <DashboardWrapper />
    </DashboardAnalyticsProvider>
  )
}
